/**
 * MyStarterGuidePage — bundle delivery page for included Starter Guide buyers.
 *
 * Gated on user.owns_starter_guide === true.
 * Non-owners are redirected to the sales page at /starter-guide.
 *
 * Displays:
 *  - 30-page PDF download (the magazine edition)
 *  - 7 walk-through videos (YouTube embeds — Mike fills in the IDs as he records)
 *  - 6 bonus PDFs (download links)
 *  - Quick-start checklist
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import { Download, Video, BookOpen, CheckCircle2, Sparkles, Lock, ArrowRight } from "lucide-react";

const NAVY = "#0F172A";
const GOLD = "#2E9F3A";
const RED = "#C9202E";
const CREAM = "#FFFFFF";

const VIDEOS = [
  { num: 1, title: "Welcome + The Math",         runtime: "~5 min", desc: "The Promise. The math behind  in 30 days.", youtube: "" },
  { num: 2, title: "Week 1 — Set The Trap",      runtime: "~7 min", desc: "Build your Mirror Site, first funnel, 7-day email sequence.", youtube: "" },
  { num: 3, title: "Week 2 — Fill The Trap",     runtime: "~5 min", desc: "The 5 free traffic places, daily.", youtube: "" },
  { num: 4, title: "Week 3 — Convert The Trap",  runtime: "~7 min", desc: "The 7-day email sequence does the selling.", youtube: "" },
  { num: 5, title: "Week 4 — Rinse & Repeat",    runtime: "~5 min", desc: "Find the winner. 10x it. Cross .", youtube: "" },
  { num: 6, title: "The Upsell Ladder",          runtime: "~5 min", desc: "included → $49 → $79 → $1,073/year per buyer.", youtube: "" },
  { num: 7, title: "Your First Day",             runtime: "~5 min", desc: "6 things to do today. Noah Principle close.", youtube: "" },
];

// Backend serves PDFs from /api/uploads/scripts/print/*
const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const PDF = (name) => `${BACKEND}/api/uploads/scripts/print/${name}`;

const BONUSES = [
  { num: 1, title: "5 Headlines That Win",       desc: "Swipe file.",                    file: "starter-bundle-spec-for-canva.pdf" },
  { num: 2, title: "3 Free Giveaway Ideas",      desc: "Quick-start ideas.",             file: "starter-bundle-spec-for-canva.pdf" },
  { num: 3, title: "My First Email",             desc: "Copy-paste template.",           file: "starter-7-video-bundle.pdf" },
  { num: 4, title: "60-Second YouTube Script",   desc: "One-take camera script.",        file: "starter-7-video-bundle.pdf" },
  { num: 5, title: "Mike's Cheat Sheet",         desc: "Laminate. Tape to monitor.",     file: "starter-bundle-spec-for-canva.pdf" },
  { num: 6, title: "30-Day Tear-Out Checklist",  desc: "Refrigerator-ready.",            file: "starter-bundle-spec-for-canva.pdf" },
];

export default function MyStarterGuidePage() {
  const { user, loading: userLoading } = useAuth();
  const nav = useNavigate();
  const [checkedAccess, setCheckedAccess] = useState(false);

  useEffect(() => {
    if (userLoading) return;
    if (!user) { nav("/starter-guide"); return; }
    // Admins always have access. Otherwise must own the bundle.
    if (!user.is_admin && !user.owns_starter_guide) { nav("/starter-guide"); return; }
    setCheckedAccess(true);
  }, [user, userLoading, nav]);

  if (!checkedAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: CREAM }}>
        <div className="text-foreground/60">Loading your bundle…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: CREAM }}>
      <SiteNav />

      {/* ───────────── WELCOME HERO ───────────── */}
      <section className="relative overflow-hidden" style={{ background: "#FFFFFF" }}>
        <div className="relative max-w-5xl mx-auto px-5 md:px-8 py-12 md:py-16 text-center">
          <Sparkles className="w-10 h-10 mx-auto mb-3" style={{ color: GOLD }} />
          <div className="text-[11px] tracking-[0.28em] uppercase font-bold mb-2" style={{ color: GOLD }}>
            ★ Amen! You're In.
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-white uppercase leading-tight">
            Your Starter Guide<br/><span style={{ color: GOLD }}>Is Ready.</span>
          </h1>
          <div className="h-px w-16 mx-auto mt-4" style={{ background: GOLD }} />
          <p className="text-base md:text-lg mt-5 max-w-2xl mx-auto" style={{ color: "#E6E2D2" }}>
            Welcome aboard, friend. Below is everything you bought — print the PDF, queue up the videos,
            and grab the bonuses. Take it at your own pace. The boat ain't going anywhere without you.
          </p>
        </div>
      </section>

      {/* ───────────── THE BIG PDF ───────────── */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 py-12">
        <div className="text-[11px] tracking-[0.28em] uppercase font-bold" style={{ color: GOLD }}>★ Step 1 · Read This First</div>
        <h2 className="font-serif-display text-3xl md:text-4xl text-red-wine uppercase mt-2">The 30-Page Game Plan</h2>
        <div className="h-px w-16 mt-3" style={{ background: GOLD }} />

        <div className="mt-6 rounded-2xl border-2 p-6 md:p-8 grid md:grid-cols-3 gap-6 items-center"
             style={{ borderColor: NAVY, background: "white" }} data-testid="starter-pdf-card">
          <div className="md:col-span-2">
            <div className="font-bold text-lg text-red-wine">Faitharama Starter Guide — Magazine Edition</div>
            <p className="text-base text-foreground/85 mt-2 leading-relaxed">
              The full 4-week plan as a 30-page magazine-style PDF. Print it. Mark it up.
              Keep it on your desk. Every section maps to one of the 7 walk-through videos below.
            </p>
            <a
              href={PDF("starter-guide-magazine.pdf")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="starter-pdf-download"
              className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-md font-bold uppercase tracking-[0.18em] text-sm shadow-lg hover:-translate-y-0.5 transition"
              style={{ background: RED, color: "white" }}
            >
              <Download className="w-4 h-4" /> Download PDF · 30 Pages
            </a>
          </div>
          <div className="hidden md:flex justify-center">
            <BookOpen className="w-24 h-24" style={{ color: GOLD }} />
          </div>
        </div>
      </section>

      {/* ───────────── 7 VIDEOS ───────────── */}
      <section className="py-12" style={{ background: "#F4ECD8" }}>
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <div className="text-[11px] tracking-[0.28em] uppercase font-bold" style={{ color: RED }}>★ Step 2 · Watch In Order</div>
          <h2 className="font-serif-display text-3xl md:text-4xl text-red-wine uppercase mt-2">The 7 Walk-Through Videos</h2>
          <div className="h-px w-16 mt-3" style={{ background: GOLD }} />
          <p className="text-base text-foreground mt-3 max-w-2xl">
            Watch one a day for a week. Each video lines up with a chapter in the PDF.
          </p>

          <div className="space-y-4 mt-7">
            {VIDEOS.map((v) => (
              <div key={v.num} className="rounded-xl bg-white border p-5 md:p-6 shadow-sm"
                   style={{ borderColor: "rgba(19,37,74,0.15)" }} data-testid={`my-starter-video-${v.num}`}>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-14 h-14 rounded-lg flex flex-col items-center justify-center font-bold"
                       style={{ background: "#FFFFFF", color: GOLD }}>
                    <Video className="w-4 h-4 mb-0.5" />
                    <div className="text-sm">{v.num}</div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <div className="font-bold text-lg text-red-wine">{v.title}</div>
                      <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: GOLD }}>{v.runtime}</div>
                    </div>
                    <p className="text-base text-foreground/85 mt-1">{v.desc}</p>

                    {v.youtube ? (
                      <div className="mt-4 relative rounded-lg overflow-hidden bg-black" style={{ aspectRatio: "16 / 9" }}>
                        <iframe
                          src={`https://www.youtube.com/embed/${v.youtube}?rel=0&modestbranding=1`}
                          title={v.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="absolute inset-0 w-full h-full"
                        />
                      </div>
                    ) : (
                      <div className="mt-3 rounded-lg p-4 text-center" style={{ background: "#EAF6FC", border: "1px dashed rgba(243,154,31,0.5)" }}>
                        <div className="text-sm font-bold" style={{ color: "#9C6B14" }}>★ Video uploading soon</div>
                        <div className="text-xs text-foreground/60 mt-1">Mike is recording this video this week. Check back shortly.</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── 6 BONUSES ───────────── */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 py-12">
        <div className="text-[11px] tracking-[0.28em] uppercase font-bold" style={{ color: GOLD }}>★ Step 3 · Stack The Bonuses</div>
        <h2 className="font-serif-display text-3xl md:text-4xl text-red-wine uppercase mt-2">Your 6 Bonus PDFs</h2>
        <div className="h-px w-16 mt-3" style={{ background: GOLD }} />

        <div className="grid md:grid-cols-2 gap-3 mt-7">
          {BONUSES.map((b) => (
            <a
              key={b.num}
              href={PDF(b.file)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`my-starter-bonus-${b.num}`}
              className="group rounded-lg bg-[#F4ECD8] border p-4 flex gap-3 items-center hover:bg-[#EFE3C2] transition"
              style={{ borderColor: "rgba(19,37,74,0.15)" }}
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm"
                   style={{ background: GOLD, color: NAVY }}>
                {b.num}
              </div>
              <div className="flex-1">
                <div className="font-bold text-sm text-red-wine">{b.title}</div>
                <p className="text-xs text-foreground/70 mt-0.5">{b.desc}</p>
              </div>
              <Download className="w-4 h-4 text-foreground/40 group-hover:text-red-wine transition" />
            </a>
          ))}
        </div>
      </section>

      {/* ───────────── NEXT STEPS ───────────── */}
      <section className="py-12" style={{ background: "#FFFFFF" }}>
        <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
          <div className="text-[11px] tracking-[0.28em] uppercase font-bold" style={{ color: GOLD }}>★ When You're Ready</div>
          <h2 className="font-serif-display text-3xl text-white uppercase mt-2">Raise The Hammer.</h2>
          <div className="h-px w-16 mt-3 mx-auto" style={{ background: GOLD }} />
          <p className="text-base mt-5" style={{ color: "#E6E2D2" }}>
            Once you've worked through the guide, the next step is the $49/mo Pro tier —
            30 AI Writer drafts a day, 2,500 contacts, and a 50% affiliate commission. Same hammer, bigger nails.
          </p>
          <Link
            to="/pricing"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md font-bold uppercase tracking-[0.18em] text-sm hover:-translate-y-0.5 transition"
            style={{ background: GOLD, color: NAVY }}
          >
            See The Tiers <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
